(() => {
  const doc = document;
  const body = doc.body;
  const drawerOverlay = doc.querySelector('.drawer-overlay');
  let revealObserver = null;
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const qs = (selector, root = doc) => root.querySelector(selector);
  const qsa = (selector, root = doc) => Array.from(root.querySelectorAll(selector));

  const escapeHtml = (value = '') =>
    String(value).replace(/[&<>"']/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    })[character]);

  const routeWithJson = (route, fallback) => {
    const base = route || fallback;
    return base.endsWith('.js') || base.endsWith('.json') ? base : `${base}.js`;
  };

  const formatMoney = (cents) => {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }

    const currency = window.Shopify?.currency?.active || 'USD';
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency
    }).format(cents / 100);
  };

  const hidePreloader = () => {
    const preloader = qs('[data-preloader]');
    if (!preloader) return;

    window.setTimeout(() => {
      preloader.classList.add('is-hidden');
    }, 450);

    window.setTimeout(() => {
      preloader.remove();
    }, 1400);
  };

  if (doc.readyState === 'complete') {
    hidePreloader();
  } else {
    window.addEventListener('load', hidePreloader, { once: true });
  }

  const getDrawer = (name) => qs(`[data-drawer="${name}"]`);

  const closeDrawers = () => {
    qsa('[data-drawer].is-open').forEach((drawer) => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
    });

    if (drawerOverlay) {
      drawerOverlay.classList.remove('is-visible');
      drawerOverlay.hidden = true;
    }

    body.classList.remove('drawer-open');
  };

  const openDrawer = (name) => {
    const drawer = getDrawer(name);
    if (!drawer) return;

    closeDrawers();
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    body.classList.add('drawer-open');

    if (drawerOverlay) {
      drawerOverlay.hidden = false;
      requestAnimationFrame(() => drawerOverlay.classList.add('is-visible'));
    }

    const focusable = drawer.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (focusable) focusable.focus({ preventScroll: true });
  };

  doc.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      event.preventDefault();
      openDrawer(opener.getAttribute('data-drawer-open'));
      return;
    }

    if (event.target.closest('[data-drawer-close]')) {
      event.preventDefault();
      closeDrawers();
    }
  });

  doc.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeDrawers();
  });

  const header = qs('[data-site-header]');
  const updateHeader = () => {
    if (!header) return;
    header.classList.toggle('is-scrolled', window.scrollY > 8);
  };
  updateHeader();
  window.addEventListener('scroll', updateHeader, { passive: true });

  const observeReveal = (root = doc) => {
    const elements = qsa('[data-reveal-section]:not(.is-visible)', root);
    if (!elements.length) return;

    if (!('IntersectionObserver' in window)) {
      elements.forEach((element) => element.classList.add('is-visible'));
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        });
      }, {
        threshold: 0.12,
        rootMargin: '0px 0px -8% 0px'
      });
    }

    elements.forEach((element) => revealObserver.observe(element));
  };

  observeReveal();

  const initEditorialDeck = () => {
    const deck = qs('[data-editorial-deck]');
    if (!deck || prefersReducedMotion) return;

    const cards = qsa('[data-deck-card]', deck);
    if (cards.length < 2) return;

    const dots = qsa('[data-deck-go]', deck);
    const current = qs('[data-deck-current]', deck);
    let activeIndex = 0;
    let isAnimating = false;
    let touchStartY = 0;

    const setDeckTheme = () => {
      const activeCard = cards[activeIndex];
      const lightCard = activeCard && !activeCard.classList.contains('editorial-panel');
      body.classList.toggle('deck-light', Boolean(lightCard));
    };

    const setActiveCard = (nextIndex) => {
      const clampedIndex = Math.max(0, Math.min(cards.length - 1, nextIndex));
      activeIndex = clampedIndex;

      cards.forEach((card, index) => {
        card.classList.toggle('is-active', index === activeIndex);
        card.classList.toggle('is-before', index < activeIndex);
        card.classList.toggle('is-after', index > activeIndex);
      });

      dots.forEach((dot, index) => {
        dot.classList.toggle('is-active', index === activeIndex);
      });

      if (current) current.textContent = String(activeIndex + 1).padStart(2, '0');
      deck.dataset.activeIndex = String(activeIndex);
      setDeckTheme();
    };

    const stepDeck = (direction) => {
      if (isAnimating) return false;
      const nextIndex = activeIndex + direction;
      if (nextIndex < 0 || nextIndex >= cards.length) return false;

      isAnimating = true;
      setActiveCard(nextIndex);
      window.setTimeout(() => {
        isAnimating = false;
      }, 880);
      return true;
    };

    const deckIsPrimaryViewport = () => {
      const rect = deck.getBoundingClientRect();
      return rect.top <= 4 && rect.bottom >= window.innerHeight * 0.72;
    };

    window.addEventListener('wheel', (event) => {
      if (!deckIsPrimaryViewport()) return;
      if (event.target.closest('input, textarea, select, [data-drawer]')) return;

      const direction = event.deltaY > 0 ? 1 : -1;
      const canMoveInsideDeck =
        (direction > 0 && activeIndex < cards.length - 1) ||
        (direction < 0 && activeIndex > 0);

      if (!canMoveInsideDeck) return;

      event.preventDefault();
      stepDeck(direction);
    }, { passive: false });

    deck.addEventListener('touchstart', (event) => {
      touchStartY = event.touches[0]?.clientY || 0;
    }, { passive: true });

    deck.addEventListener('touchend', (event) => {
      const touchEndY = event.changedTouches[0]?.clientY || 0;
      const delta = touchStartY - touchEndY;
      if (Math.abs(delta) < 42) return;

      const direction = delta > 0 ? 1 : -1;
      const moved = stepDeck(direction);
      if (moved) event.preventDefault();
    });

    doc.addEventListener('keydown', (event) => {
      if (!deckIsPrimaryViewport()) return;
      if (['ArrowDown', 'PageDown', ' '].includes(event.key)) {
        if (activeIndex < cards.length - 1) {
          event.preventDefault();
          stepDeck(1);
        }
      }

      if (['ArrowUp', 'PageUp'].includes(event.key)) {
        if (activeIndex > 0) {
          event.preventDefault();
          stepDeck(-1);
        }
      }
    });

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const nextIndex = Number(dot.dataset.deckGo);
        if (Number.isNaN(nextIndex)) return;
        deck.scrollIntoView({ block: 'start' });
        setActiveCard(nextIndex);
      });
    });

    setActiveCard(0);
  };

  initEditorialDeck();

  const updateCartBadges = (cart) => {
    qsa('[data-cart-count]').forEach((element) => {
      element.textContent = cart.item_count;
    });

    const cartTotal = qs('[data-cart-total]');
    if (cartTotal) cartTotal.textContent = formatMoney(cart.total_price);
  };

  const renderCartItems = (cart) => {
    const container = qs('[data-cart-items]');
    if (!container) return;

    if (!cart.items.length) {
      container.innerHTML = `
        <div class="cart-empty">
          <p>${escapeHtml(window.themeStrings?.cartEmpty || 'Your shopping bag is empty.')}</p>
          <a class="button button--full" href="/collections/all">${escapeHtml(window.themeStrings?.continueShopping || 'Continue shopping')}</a>
        </div>
      `;
      return;
    }

    container.innerHTML = cart.items.map((item, index) => {
      const image = item.image
        ? `<img class="cart-item__image" src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title)}" loading="lazy">`
        : '';
      const variant = item.variant_title && item.variant_title !== 'Default Title'
        ? `<p class="cart-item__variant">${escapeHtml(item.variant_title)}</p>`
        : '';
      const quantity = item.quantity;

      return `
        <article class="cart-item" data-cart-item>
          <a class="cart-item__media" href="${escapeHtml(item.url)}">${image}</a>
          <div class="cart-item__content">
            <a class="cart-item__title" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
            ${variant}
            <p class="cart-item__price">${formatMoney(item.final_line_price)}</p>
            <div class="quantity-control">
              <button type="button" data-cart-change data-line="${index + 1}" data-quantity="${quantity - 1}" aria-label="Decrease quantity">−</button>
              <span>${quantity}</span>
              <button type="button" data-cart-change data-line="${index + 1}" data-quantity="${quantity + 1}" aria-label="Increase quantity">+</button>
            </div>
            <button type="button" class="text-button" data-cart-change data-line="${index + 1}" data-quantity="0">Remove</button>
          </div>
        </article>
      `;
    }).join('');
  };

  const getCart = async () => {
    const response = await fetch(routeWithJson(window.themeRoutes?.cart, '/cart'), {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Could not load cart');
    return response.json();
  };

  const refreshCart = async () => {
    const cart = await getCart();
    updateCartBadges(cart);
    renderCartItems(cart);
    const checkoutButton = qs('.cart-drawer__footer button[name="checkout"]');
    if (checkoutButton) checkoutButton.disabled = cart.item_count === 0;
    return cart;
  };

  const setFormState = (form, loading, message = '') => {
    const button = qs('[data-add-to-cart]', form);
    const messageElement = qs('[data-form-message]', form);

    if (button) {
      button.disabled = loading;
      button.textContent = loading
        ? (window.themeStrings?.adding || 'Adding')
        : (button.dataset.defaultText || window.themeStrings?.addToCart || 'Add to bag');
    }

    if (messageElement) messageElement.textContent = message;
  };

  qsa('[data-add-to-cart]').forEach((button) => {
    button.dataset.defaultText = button.textContent.trim();
  });

  doc.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-product-form], [data-quick-add-form]');
    if (!form) return;

    event.preventDefault();
    setFormState(form, true);

    try {
      const response = await fetch(routeWithJson(window.themeRoutes?.cartAdd, '/cart/add'), {
        method: 'POST',
        body: new FormData(form),
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || error.message || 'Unable to add item');
      }

      await refreshCart();
      setFormState(form, false);

      if (body.dataset.cartDrawerEnabled === 'true') {
        openDrawer('cart');
      }
    } catch (error) {
      setFormState(form, false, error.message);
    }
  });

  doc.addEventListener('click', async (event) => {
    const control = event.target.closest('[data-cart-change]');
    if (!control) return;

    event.preventDefault();
    control.disabled = true;

    try {
      const response = await fetch(routeWithJson(window.themeRoutes?.cartChange, '/cart/change'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          line: Number(control.dataset.line),
          quantity: Number(control.dataset.quantity)
        })
      });

      if (!response.ok) throw new Error('Unable to update cart');
      const cart = await response.json();
      updateCartBadges(cart);
      renderCartItems(cart);
    } catch (error) {
      control.disabled = false;
    }
  });

  qsa('[data-product-form]').forEach((form) => {
    const picker = qs('[data-variant-picker]', form);
    const variantScript = qs('[data-variant-json]', form);
    if (!picker || !variantScript) return;

    const variants = JSON.parse(variantScript.textContent);
    const hiddenInput = qs('[data-variant-id]', form);
    const button = qs('[data-add-to-cart]', form);

    const updateVariant = () => {
      const selectedOptions = qsa('fieldset', picker).map((fieldset) => {
        const checked = qs('input:checked', fieldset);
        return checked ? checked.value : null;
      });

      const selectedVariant = variants.find((variant) =>
        variant.options.every((option, index) => option === selectedOptions[index])
      );

      if (hiddenInput && selectedVariant) hiddenInput.value = selectedVariant.id;

      if (button) {
        const available = Boolean(selectedVariant && selectedVariant.available);
        button.disabled = !available;
        button.dataset.defaultText = available
          ? (window.themeStrings?.addToCart || 'Add to bag')
          : (selectedVariant ? window.themeStrings?.soldOut : window.themeStrings?.unavailable);
        button.textContent = button.dataset.defaultText;
      }
    };

    picker.addEventListener('change', updateVariant);
    updateVariant();
  });

  const debounce = (callback, delay = 180) => {
    let timeout;
    return (...args) => {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => callback(...args), delay);
    };
  };

  qsa('[data-predictive-search-input]').forEach((input) => {
    const drawer = input.closest('.search-drawer');
    const results = qs('[data-predictive-search-results]', drawer);
    if (!results) return;

    const search = debounce(async () => {
      const query = input.value.trim();
      if (query.length < 2) {
        results.innerHTML = '';
        return;
      }

      const endpoint = `${window.themeRoutes?.predictiveSearch || '/search/suggest'}.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6`;
      const response = await fetch(endpoint, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const data = await response.json();
      const products = data.resources?.results?.products || [];

      if (!products.length) {
        results.innerHTML = `<p>${escapeHtml(window.themeStrings?.noResults || 'No results found')}</p>`;
        return;
      }

      results.innerHTML = products.map((product) => `
        <a class="predictive-search__item" href="${escapeHtml(product.url)}">
          ${product.image ? `<img class="predictive-search__image" src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" loading="lazy">` : ''}
          <span>
            <strong>${escapeHtml(product.title)}</strong>
            ${product.price ? `<small>${escapeHtml(product.price)}</small>` : ''}
          </span>
        </a>
      `).join('');
    });

    input.addEventListener('input', search);
  });

  const filterToggle = qs('[data-filter-toggle]');
  const filterForm = qs('[data-filter-form]');
  if (filterToggle && filterForm) {
    filterToggle.addEventListener('click', () => {
      filterForm.classList.toggle('is-open');
    });
  }

  if (filterForm) {
    qsa('select, input[type="checkbox"]', filterForm).forEach((field) => {
      field.addEventListener('change', () => filterForm.submit());
    });
  }

  doc.addEventListener('click', async (event) => {
    const link = event.target.closest('[data-load-more]');
    if (!link) return;

    event.preventDefault();
    link.classList.add('is-loading');
    link.textContent = window.themeStrings?.adding || 'Loading';

    try {
      const response = await fetch(link.href);
      if (!response.ok) throw new Error('Unable to load more products');
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const nextItems = qsa('[data-product-grid] > *', parsed);
      const currentGrid = qs('[data-product-grid]');

      nextItems.forEach((item) => currentGrid.appendChild(item));

      const nextPagination = qs('[data-pagination]', parsed);
      const currentPagination = qs('[data-pagination]');
      if (currentPagination) {
        currentPagination.innerHTML = nextPagination ? nextPagination.innerHTML : '';
      }

      observeReveal(currentGrid);
    } catch (error) {
      link.textContent = 'Try again';
      link.classList.remove('is-loading');
    }
  });
})();
